import React, { useState, useEffect } from "react";
import api from "../Service/api";
import "./ViewClientRequirements.css";

const ViewClientRequirements = () => {
  const [companyName, setCompanyName] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editingContact, setEditingContact] = useState(false);
  const [editContactPerson, setEditContactPerson] = useState("");
  const [editContactNumber, setEditContactNumber] = useState("");

  const [editingRequirementId, setEditingRequirementId] = useState(null);
  const [editRequirement, setEditRequirement] = useState("");

  // Fetch all company names
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await api.get("/api/company-requirements/companyNames");
        if (res.data?.code === 1 && res.data?.additionalDetail) {
          setCompanyList(res.data.additionalDetail);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch company details
  const fetchDetails = async () => {
    if (!companyName) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/api/company-requirements/${companyName}`);
      setData(res.data || []);
      if (res.data.length > 0) {
        setEditContactPerson(res.data[0].contactPerson);
        setEditContactNumber(res.data[0].contactNumber);
      }
      setEditingContact(false);
    } catch {
      setError("No record found for this company");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const startEditRequirement = (item) => {
    setEditingRequirementId(item.id);
    setEditRequirement(item.requirements);
  };

  const saveRequirement = async (id) => {
    try {
      await api.put(`/api/company-requirements/${id}`, { requirements: editRequirement });
      setData(data.map(item => item.id === id ? { ...item, requirements: editRequirement } : item));
      setEditingRequirementId(null);
      setEditRequirement("");
    } catch {
      alert("Failed to update requirement");
    }
  };

  const cancelRequirementEdit = () => {
    setEditingRequirementId(null);
    setEditRequirement("");
  };

  const deleteRequirement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this requirement?")) return;
    try {
      await api.delete(`/api/company-requirements/${id}`);
      setData(data.filter(item => item.id !== id));
    } catch {
      alert("Failed to delete record");
    }
  };

  const startEditContact = () => setEditingContact(true);
  const cancelEditContact = () => {
    if (data.length > 0) {
      setEditContactPerson(data[0].contactPerson);
      setEditContactNumber(data[0].contactNumber);
    }
    setEditingContact(false);
  };
  
  const saveEditContact = async () => {
    try {
      await api.put("/api/company-requirements/updateContactByCompany", {
        oldCompanyName: companyName,
        contactPerson: editContactPerson,
        contactNumber: editContactNumber
      });
      setData(data.map(item => ({
        ...item,
        contactPerson: editContactPerson,
        contactNumber: editContactNumber
      })));
      setEditingContact(false);
      alert("✅ Contact info updated for all records");
    } catch {
      alert("❌ Failed to update contact info");
    }
  };

  // Print individual requirement
  const printRequirement = (requirementItem) => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Client Requirement - ${requirementItem.companyName}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
            color: #000;
            line-height: 1.6;
            background-color: #fff;
          }
          .print-header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #333;
          }
          .print-header h1 {
            color: #1a365d;
            margin-bottom: 15px;
            font-size: 28px;
          }
          .company-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding: 20px;
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }
          .info-block {
            flex: 1;
          }
          .info-label {
            font-weight: bold;
            color: #4a5568;
            margin-bottom: 5px;
            font-size: 14px;
          }
          .info-value {
            color: #000;
            font-size: 16px;
          }
          .requirement-content {
            margin-top: 30px;
            padding: 25px;
            background-color: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            min-height: 300px;
          }
          .requirement-title {
            color: #2d3748;
            margin-bottom: 20px;
            font-size: 20px;
            font-weight: bold;
            border-bottom: 2px solid #cbd5e0;
            padding-bottom: 10px;
          }
          .requirement-text {
            color: #000;
            font-size: 16px;
            white-space: pre-wrap;
            line-height: 1.8;
          }
          .requirement-images img {
            max-width: 300px;
            max-height: 200px;
            border: 1px solid #ccc;
            border-radius: 4px;
            margin-top: 15px;
          }
          .footer {
            margin-top: 50px;
            padding-top: 20px;
            border-top: 2px solid #e2e8f0;
            text-align: center;
            font-size: 12px;
            color: #718096;
          }
          .print-date {
            margin-top: 30px;
            text-align: right;
            color: #4a5568;
            font-size: 14px;
          }
          @media print {
            body {
              padding: 25px;
            }
            @page {
              margin: 0.5in;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>📄 CLIENT REQUIREMENT DOCUMENT</h1>
        </div>
        
        <div class="company-info">
          <div class="info-block">
            <div class="info-label">Company Name</div>
            <div class="info-value">${requirementItem.companyName}</div>
          </div>
          <div class="info-block">
            <div class="info-label">Contact Person</div>
            <div class="info-value">${requirementItem.contactPerson}</div>
          </div>
          <div class="info-block">
            <div class="info-label">Contact Number</div>
            <div class="info-value">${requirementItem.contactNumber}</div>
          </div>
        </div>
        
        <div class="requirement-content">
          <div class="requirement-title">📋 Requirement Details</div>
          <div class="requirement-text">${requirementItem.requirements}</div>
          <div class="requirement-images">
${requirementItem.frontImagePath ? `<div><div class="image-label">Front Image</div><img src="${requirementItem.frontImagePath}" alt="Front Image" /></div>` : ''}
            ${requirementItem.backImagePath ? `<div><div class="image-label">Back Image</div><img src="${requirementItem.backImagePath}" alt="Back Image" /></div>` : ''}
          </div>
        </div>
        
        <div class="print-date">
          Printed on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
        </div>
        
        <div class="footer">
          <p>--- End of Requirement Document ---</p>
          <p>Document ID: ${requirementItem.id} | Generated by: ${requirementItem.companyName}</p>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }, 250);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Print all requirements for the company
  const printAllRequirements = () => {
    if (data.length === 0) return;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    const requirementsList = data.map((item, index) => `
      <div class="requirement-item" style="margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <h3 style="color: #2d3748; font-size: 18px;">Requirement #${index + 1}</h3>
          <span style="font-size: 12px; color: #718096;">ID: ${item.id}</span>
        </div>
        <div style="color: #000; font-size: 15px; line-height: 1.6; white-space: pre-wrap; padding: 15px; background-color: #f8fafc; border-radius: 6px;">
          ${item.requirements}
        </div>
       ${item.frontImagePath ? `<div style="margin-top:10px;"><strong>Front Image</strong><br /><img src="${item.frontImagePath}" style="max-width:300px; max-height:200px; border:1px solid #ccc; border-radius:4px;" alt="Front Image" /></div>` : ''}
        ${item.backImagePath ? `<div style="margin-top:10px;"><strong>Back Image</strong><br /><img src="${item.backImagePath}" style="max-width:300px; max-height:200px; border:1px solid #ccc; border-radius:4px;" alt="Back Image" /></div>` : ''}
      </div>
    `).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>All Requirements - ${data[0].companyName}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family: Arial,sans-serif; padding:30px; color:#000; line-height:1.6; background-color:#fff; }
          .print-header { text-align:center; margin-bottom:40px; padding-bottom:20px; border-bottom:3px solid #333; }
          .print-header h1 { color:#1a365d; margin-bottom:10px; font-size:28px; }
          .company-summary { display:flex; justify-content:space-between; margin-bottom:30px; padding:20px; background-color:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; }
          .summary-block { text-align:center; flex:1; }
          .summary-label { font-weight:bold; color:#4a5568; margin-bottom:5px; font-size:14px; }
          .summary-value { color:#000; font-size:16px; }
          .total-count { background-color:#1a365d; color:white; padding:5px 15px; border-radius:20px; font-weight:bold; }
          .requirements-list { margin-top:30px; }
          .requirements-title { color:#2d3748; margin-bottom:20px; font-size:22px; font-weight:bold; border-bottom:2px solid #cbd5e0; padding-bottom:10px; }
          .footer { margin-top:50px; padding-top:20px; border-top:2px solid #e2e8f0; text-align:center; font-size:12px; color:#718096; }
          .print-date { margin-top:30px; text-align:right; color:#4a5568; font-size:14px; }
          .requirement-item img { max-width:300px; max-height:200px; border:1px solid #ccc; border-radius:4px; margin-top:10px; }
          @media print { body { padding:25px; } .requirement-item { page-break-inside: avoid; } @page { margin:0.5in; } }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>📋 ALL CLIENT REQUIREMENTS</h1>
          <p style="color: #4a5568;">Company: ${data[0].companyName}</p>
        </div>
        
        <div class="company-summary">
          <div class="summary-block">
            <div class="summary-label">Contact Person</div>
            <div class="summary-value">${data[0].contactPerson}</div>
          </div>
          <div class="summary-block">
            <div class="summary-label">Contact Number</div>
            <div class="summary-value">${data[0].contactNumber}</div>
          </div>
          <div class="summary-block">
            <div class="summary-label">Total Requirements</div>
            <div class="summary-value"><span class="total-count">${data.length}</span></div>
          </div>
        </div>
        
        <div class="requirements-list">
          <div class="requirements-title">📄 Requirements List</div>
          ${requirementsList}
        </div>
        
        <div class="print-date">
          Printed on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
        </div>
        
        <div class="footer">
          <p>--- End of Requirements Document ---</p>
          <p>Generated for: ${data[0].companyName} | Total Requirements: ${data.length}</p>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }, 250);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <div className="client-view-container">
      <div style={{ marginBottom: "20px" }}>
        <h1 className="page-title">📄 View Client Requirements</h1>
        {data.length > 0 && (
          <div style={{ textAlign: "right", marginTop: "10px" }}>
            <button
              onClick={printAllRequirements}
              style={{
                padding: "8px 20px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 500,
                fontSize: 14
              }}
            >
              📋 Print All Requirements
            </button>
          </div>
        )}
      </div>

      <div className="client-search-box">
        <select value={companyName} onChange={(e) => setCompanyName(e.target.value)}>
          <option value="">-- Select Company --</option>
          {companyList.map((name, idx) => <option key={idx} value={name}>{name}</option>)}
        </select>
        <button onClick={fetchDetails}>{loading ? "Searching..." : "Search"}</button>
      </div>

      {error && <div className="client-error">{error}</div>}

      {data.length > 0 && (
        <div className="client-profile">
          <div className="client-header">{data[0].companyName}</div>

          <div className="client-info" style={{ alignItems: "center" }}>
            <div>
              <span>Contact Person:</span>
              {editingContact ? (
                <input type="text" value={editContactPerson} onChange={(e) => setEditContactPerson(e.target.value)} />
              ) : <strong>{data[0].contactPerson}</strong>}
            </div>
            <div>
              <span>Contact Number:</span>
              {editingContact ? (
                <input type="text" value={editContactNumber} onChange={(e) => setEditContactNumber(e.target.value)} />
              ) : <strong>{data[0].contactNumber}</strong>}
            </div>
            <div>
              {editingContact ? (
                <>
                  <button className="save-btn" onClick={saveEditContact}>Save</button>
                  <button className="cancel-btn" onClick={cancelEditContact}>Cancel</button>
                </>
              ) : (
                <button className="update-btn" onClick={startEditContact}>Update</button>
              )}
            </div>
          </div>

          <div className="client-requirements">
            <div className="section-title">Business Requirements</div>
            {data.map(item => (
              <div key={item.id} className="requirement-card">
                {editingRequirementId === item.id ? (
                  <div className="edit-requirement">
                    <textarea
                      value={editRequirement}
                      onChange={(e) => setEditRequirement(e.target.value)}
                      rows={2}
                    />
                    <div className="requirement-actions">
                      <button className="save-btn" onClick={() => saveRequirement(item.id)}>Save</button>
                      <button className="cancel-btn" onClick={cancelRequirementEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="requirement-text">{item.requirements}</div>
                    {item.frontImagePath && <div style={{ marginTop: "10px" }}><strong>Front Image</strong><br /><img src={item.frontImagePath} alt="Front" style={{ maxWidth: 200, maxHeight: 150, border: "1px solid #ccc", borderRadius: 4 }} /></div>}
                    {item.backImagePath && <div style={{ marginTop: "10px" }}><strong>Back Image</strong><br /><img src={item.backImagePath} alt="Back" style={{ maxWidth: 200, maxHeight: 150, border: "1px solid #ccc", borderRadius: 4 }} /></div>}

                    <div className="requirement-actions">
                      <button className="update-btn" onClick={() => startEditRequirement(item)}>Update</button>
                      <button className="delete-btn" onClick={() => deleteRequirement(item.id)}>Delete</button>
                      <button className="print-btn" onClick={() => printRequirement(item)}>🖨️ Print</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewClientRequirements;
